import { Note, Topic } from '@/types'

import {
  isDefaultValue,
  MandatoryNoteTypes,
  NoteType,
  PatternAssetTypesData,
} from '@/utils/constants'
import { Box, Paper, Stack, Typography, Chip, Divider } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import React, { useEffect, useState } from 'react'
import { SVGS } from '@/assets/svgs'
import SVG from 'react-inlinesvg'

interface Props {
  topic: Topic
  onClose?: () => void
}

const patternNoteTypes: Record<string, string> = {
  accentID: '6',
  print: '4',
  color: '5',
  materialID: '3',
  sceneID: '7',
  merge: '10',
  position: '11',
  accentScale: '13',
  accentNumber: '14',
  accentRotation: '15',
  matting: '17',
  printTiling: '19',
}
interface DataMap {
  noteType: NoteType
  mandatory?: boolean
  found: boolean
}

export default function PublishCheckListModal(props: Props) {
  const { topic, onClose } = props
  const [data, setData] = useState<DataMap[]>([])

  useEffect(() => {
    let tempData: DataMap[] = []

    let allValues = Object.values(patternNoteTypes)
    allValues = allValues.filter((e) => {
      let flag = false
      const noteType: NoteType = Number(e)
      const info = PatternAssetTypesData[noteType]
      flag = e != patternNoteTypes['templateID'] && info != null
      return flag
    }) // excluding templateID, no more needed

    const existingNoteTypes: NoteType[] = []
    const missingNoteTypes: NoteType[] = []
    // const allNoTypesSet = allValues.map((p) => Number(p))
    // const createdNoteTypes = new Set(topic.notes.map((p) => Number(p.noteType)))

    const hasAccent = topic.notes.some((e) => e.noteType == NoteType.ACCENT.toString())
    allValues.forEach((noteType: string) => {
      let foundNote = topic.notes.find((e) => e.noteType == noteType)

      if (!foundNote) {
        missingNoteTypes.push(Number(noteType))
      } else {
        let noteText = foundNote.noteText
        console.log('NoteText: ', noteText)
        console.log('NoteName: ', foundNote.noteName)
        let flag =
          !isDefaultValue(foundNote.noteText, foundNote.noteName, hasAccent) &&
          noteText != 'null' &&
          noteText != '{}'
        if (flag) {
          existingNoteTypes.push(Number(noteType))
        } else {
          missingNoteTypes.push(Number(noteType))
        }
      }
    })

    // const existingNoteTypes = [...allNoTypesSet].filter((type) => createdNoteTypes.has(type))
    // const missingNoteTypes = [...allNoTypesSet].filter((type) => !createdNoteTypes.has(type))

    if (existingNoteTypes.length) {
      let mapped: DataMap[] = existingNoteTypes.map((e) => {
        let isMandatory = MandatoryNoteTypes.includes(e)
        return {
          noteType: e,
          mandatory: isMandatory,
          found: true,
        }
      })

      tempData.push(...mapped)
    }

    if (missingNoteTypes.length) {
      let mapped: DataMap[] = missingNoteTypes.map((e) => {
        let isMandatory = MandatoryNoteTypes.includes(e)
        return {
          noteType: e,
          mandatory: isMandatory,
          found: false,
        }
      })

      tempData.push(...mapped)
    }
    setData(tempData)
  }, [])

  //   const relevantKeys = Object.keys(draftData).filter((draftKey) => {
  //     if (!PatternNoteTypes[draftKey]) {
  //       console.log(`⏭️ Skipping ${draftKey} - no note type mapping`)
  //       return false
  //     }
  //     return true
  //   })

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '32px',

          width: '480px',
          maxHeight: '75%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // prevents the outer box itself from scrolling/growing
        }}
      >
        {/* Fixed header */}
        <Box
          sx={{
            mb: 1.5,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography variant='h1' fontWeight={700}>
              {'Almost there'}
            </Typography>
            <Typography variant='subtitle1' color='text.secondary'>
              {'Complete the checklist below to continue'}
            </Typography>
          </Box>

          <SVG
            src={SVGS.CrossIcon}
            width={16}
            height={16}
            onClick={onClose}
            style={{ cursor: 'pointer', flexShrink: 0 }}
          />
        </Box>

        <Divider sx={{ mb: 1.5, flexShrink: 0 }} />

        {/* Scrollable list */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          <Stack spacing={1}>
            {data.map((item) => (
              <Box
                key={item.noteType}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: item.found ? 'action.hover' : 'transparent',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {item.found ? (
                    <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                  ) : (
                    <CancelIcon
                      sx={{
                        fontSize: 20,
                        color: item.mandatory ? 'error.main' : 'grey.400',
                      }}
                    />
                  )}
                  <Typography
                    variant='body2'
                    fontWeight={500}
                    textTransform={'capitalize'}
                    color={!item.found && !item.mandatory ? 'text.disabled' : 'text.primary'}
                  >
                    {PatternAssetTypesData[item.noteType]?.categoryName == 'accent'
                      ? 'Template'
                      : PatternAssetTypesData[item.noteType]?.categoryName}
                  </Typography>
                </Box>

                {item.mandatory && !item.found && (
                  <Chip
                    label='Required'
                    size='small'
                    color='error'
                    variant='outlined'
                    sx={{ height: 20, fontSize: 11 }}
                  />
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}
